import React, { useState, useEffect, useRef } from 'react';
import {
  Search,
  MapPin,
  Store,
  Star,
  CheckCircle,
  Loader2,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@vaiu/ui/card';
import { Button } from '@vaiu/ui/button';
import { Input } from '@vaiu/ui/input';
import { Label } from '@vaiu/ui/label';
import { Badge } from '@vaiu/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@vaiu/ui/select';
import { useToast } from '@/hooks/use-toast';
import { platformApi } from '@/services/platformApi';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { googleMapsLoader } from '@/utils/googleMapsLoader';
import { i18n } from '@/utils/i18n';
import { normalizeApiEntityId } from '@/utils/normalizeApiEntityId';
import {
  buildMarketplaceReturnState,
  type MarketplaceRestoreState,
} from '@/pages/marketplace/marketplaceNavigation';

interface MerchantPreview {
  id: string;
  businessName: string;
  businessType?: string;
  description?: string;
  address?: {
    city?: string;
    state?: string;
    street?: string;
  };
  rating?: number;
  reviewCount?: number;
  verified?: boolean;
  tags?: string[];
  acceptsPayments?: boolean;
}

const STORE_CATEGORIES = [
  { value: 'e-commerce', labelKey: 'marketplace.categories.ecommerce' },
  { value: 'food-beverage', labelKey: 'marketplace.categories.food_beverage' },
  { value: 'retail', labelKey: 'marketplace.categories.retail' },
  { value: 'services', labelKey: 'marketplace.categories.services' },
  { value: 'digital', labelKey: 'marketplace.categories.digital' },
  { value: 'clothing', labelKey: 'marketplace.categories.clothing' },
  { value: 'electronics', labelKey: 'marketplace.categories.electronics' },
  { value: 'books', labelKey: 'marketplace.categories.books' },
  { value: 'health', labelKey: 'marketplace.categories.health' },
  { value: 'home', labelKey: 'marketplace.categories.home' },
  { value: 'sports', labelKey: 'marketplace.categories.sports' },
  { value: 'toys', labelKey: 'marketplace.categories.toys' },
  { value: 'automotive', labelKey: 'marketplace.categories.automotive' },
  { value: 'art', labelKey: 'marketplace.categories.art' },
  { value: 'music', labelKey: 'marketplace.categories.music' },
  { value: 'other', labelKey: 'marketplace.categories.other' },
];

const MerchantCardSkeleton = () => (
  <Card className="animate-pulse">
    <CardHeader>
      <div className="h-6 bg-gray-200 rounded w-3/4"></div>
      <div className="h-4 bg-gray-200 rounded w-1/4 mt-2"></div>
    </CardHeader>
    <CardContent>
      <div className="h-4 bg-gray-200 rounded w-full mb-2"></div>
      <div className="h-4 bg-gray-200 rounded w-5/6"></div>
    </CardContent>
  </Card>
);

function optionalNumber(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

export default function UnifiedMarketplace() {
  const { toast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const [, setSearchParams] = useSearchParams();

  const [merchants, setMerchants] = useState<MerchantPreview[]>([]);
  const [filteredMerchants, setFilteredMerchants] = useState<MerchantPreview[]>([]);
  const [merchantsLoading, setMerchantsLoading] = useState(false);
  const [merchantsPage, setMerchantsPage] = useState(1);
  const [merchantsHasMore, setMerchantsHasMore] = useState(true);
  const [merchantsTotal, setMerchantsTotal] = useState(0);
  const [merchantsLoadFailed, setMerchantsLoadFailed] = useState(false);
  const merchantsPerPage = 50;

  const loadMoreTriggerRef = useRef<HTMLDivElement>(null);
  const [locationCoords, setLocationCoords] = useState<{ lat: number; lng: number } | null>(null);

  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [locationSearch, setLocationSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [locationRadius, setLocationRadius] = useState<number | 'worldwide'>(50);
  const [sortBy, setSortBy] = useState<'name' | 'rating'>('name');

  useEffect(() => {
    googleMapsLoader.load().catch(err => {
      console.error('Failed to load Google Maps API', err);
      toast({
        title: 'Could not load map service',
        description: 'Location search may not work as expected.',
        variant: 'destructive',
      });
    });
  }, [toast]);

  const loadMerchants = async (
    page: number = 1,
    append: boolean = false,
    searchOptions: {
      searchTerm?: string;
      category?: string;
      location?: string;
      coords?: { lat: number; lng: number };
      radius?: number | 'worldwide';
    }
  ) => {
    if (merchantsLoading) return;

    try {
      setMerchantsLoading(true);
      if (!append) {
        setMerchantsLoadFailed(false);
      }

      const apiParams: Record<string, unknown> = {
        limit: merchantsPerPage,
        skip: (page - 1) * merchantsPerPage,
        sort: 'relevance',
      };

      if (searchOptions.searchTerm) {
        apiParams.term = searchOptions.searchTerm;
      }
      if (searchOptions.category && searchOptions.category !== 'all') {
        apiParams.category = searchOptions.category;
      }
      if (searchOptions.location) {
        apiParams.location = searchOptions.location;
      }
      if (searchOptions.coords) {
        apiParams.lat = searchOptions.coords.lat;
        apiParams.lng = searchOptions.coords.lng;
        const radius = searchOptions.radius ?? locationRadius;
        if (radius !== 'worldwide') {
          apiParams.radius = radius;
        }
      }

      const response = await platformApi.merchant.searchMerchants(apiParams);

      let merchantsArray: unknown[] = [];
      let hasMore = false;
      let total = 0;

      if (response.merchants) {
        merchantsArray = response.merchants;
        hasMore = response.pagination?.hasMore || false;
        total = response.pagination?.total || merchantsArray.length;
      } else if (Array.isArray(response)) {
        merchantsArray = response;
        hasMore = merchantsArray.length === merchantsPerPage;
        total = merchantsArray.length;
      }

      const publicProfileId = (m: Record<string, unknown>) =>
        m.userId ||
        m.ownerId ||
        m.ownerUserId ||
        m.publicUserId ||
        (m.user as { id?: unknown } | undefined)?.id ||
        (m.owner as { id?: unknown } | undefined)?.id ||
        m.merchantOwnerId ||
        m.accountId;

      const transformedMerchants = merchantsArray.map((raw: unknown) => {
        const merchant = raw as Record<string, unknown>;
        const rating = optionalNumber(merchant.rating) ?? optionalNumber(merchant.googleRating);
        const reviewCount = optionalNumber(merchant.reviewCount) ?? optionalNumber(merchant.reviews);
        return {
          id: normalizeApiEntityId(
            publicProfileId(merchant) || merchant.id || merchant._id
          ),
          businessName: String(merchant.businessName || merchant.name || ''),
          businessType: (merchant.businessType || merchant.category) as string | undefined,
          description: typeof merchant.description === 'string' ? merchant.description : undefined,
          address: (merchant.address as MerchantPreview['address']) || {
            city: merchant.city as string | undefined,
            state: merchant.state as string | undefined,
            street: merchant.street as string | undefined,
          },
          rating,
          reviewCount: reviewCount && reviewCount > 0 ? reviewCount : undefined,
          verified: merchant.verified === true || merchant.isVerified === true,
          tags: Array.isArray(merchant.tags) ? (merchant.tags as string[]) : [],
          acceptsPayments: merchant.acceptsPayments === true,
        };
      });

      if (append) {
        setMerchants(prev => [...prev, ...transformedMerchants]);
        if (!searchQuery && selectedCategory === 'all' && !locationSearch) {
          setFilteredMerchants(prev => [...prev, ...transformedMerchants]);
        }
      } else {
        setMerchants(transformedMerchants);
        setFilteredMerchants(transformedMerchants);
      }

      setMerchantsHasMore(hasMore);
      setMerchantsTotal(total);
      setMerchantsPage(page);
    } catch (error) {
      console.error('Failed to load merchants:', error);
      if (!append) {
        setMerchants([]);
        setFilteredMerchants([]);
        setMerchantsLoadFailed(true);
      } else {
        setMerchantsHasMore(false);
      }
    } finally {
      setMerchantsLoading(false);
    }
  };

  const filterAndSortMerchants = () => {
    let filtered = merchants.filter(merchant => merchant.businessName);

    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(merchant =>
        merchant.businessName?.toLowerCase().includes(q) ||
        merchant.description?.toLowerCase().includes(q) ||
        merchant.businessType?.toLowerCase().includes(q) ||
        merchant.tags?.some(tag => tag.toLowerCase().includes(q))
      );
    }

    if (selectedCategory !== 'all') {
      filtered = filtered.filter(merchant => merchant.businessType === selectedCategory);
    }

    filtered.sort((a, b) => {
      if (sortBy === 'rating') {
        return (b.rating || 0) - (a.rating || 0);
      }
      return (a.businessName || '').localeCompare(b.businessName || '');
    });

    setFilteredMerchants(filtered);
  };

  useEffect(() => {
    const restore = (location.state as { restoreMarketplace?: MarketplaceRestoreState })
      ?.restoreMarketplace;
    if (!restore) return;

    setSearchInput(restore.searchInput);
    setSearchQuery(restore.searchQuery);
    setLocationSearch(restore.locationSearch);
    setSelectedCategory(restore.selectedCategory);
    setLocationRadius(restore.locationRadius);
    setLocationCoords(restore.locationCoords);
    setSearchParams({ tab: 'stores' });

    void loadMerchants(1, false, {
      searchTerm: restore.searchQuery || undefined,
      category: restore.selectedCategory,
      location: restore.locationSearch.trim() || undefined,
      coords: restore.locationCoords || undefined,
      radius: restore.locationRadius,
    });

    navigate({ pathname: location.pathname, search: location.search }, { replace: true, state: {} });
  }, [location.state]);

  useEffect(() => {
    if ((location.state as { restoreMarketplace?: MarketplaceRestoreState })?.restoreMarketplace) {
      return;
    }
    loadMerchants(1, false, { searchTerm: '', category: 'all' });
  }, []);

  useEffect(() => {
    if (!locationCoords) return;

    setMerchants([]);
    setFilteredMerchants([]);
    setMerchantsPage(1);
    setMerchantsHasMore(true);
    loadMerchants(1, false, { searchTerm: searchQuery, category: selectedCategory, coords: locationCoords });
  }, [locationRadius]);

  useEffect(() => {
    if (!merchantsHasMore || merchantsLoading) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && merchantsHasMore && !merchantsLoading) {
          loadMerchants(merchantsPage + 1, true, {
            searchTerm: searchQuery,
            category: selectedCategory,
            coords: locationCoords,
          });
        }
      },
      { threshold: 0.1, rootMargin: '100px' }
    );

    const currentTrigger = loadMoreTriggerRef.current;
    if (currentTrigger) {
      observer.observe(currentTrigger);
    }

    return () => {
      if (currentTrigger) {
        observer.unobserve(currentTrigger);
      }
    };
  }, [merchantsHasMore, merchantsLoading, merchantsPage, searchQuery, selectedCategory, locationCoords]);

  useEffect(() => {
    filterAndSortMerchants();
  }, [merchants, searchQuery, selectedCategory, sortBy]);

  const geocodeLocation = async (place: string): Promise<google.maps.GeocoderResult | null> => {
    if (!googleMapsLoader.isGoogleMapsLoaded() || !window.google) {
      toast({ title: 'Map service not loaded', description: 'Please wait a moment and try again.', variant: 'destructive' });
      return null;
    }
    const geocoder = new window.google.maps.Geocoder();
    try {
      const response = await geocoder.geocode({ address: place });
      if (response.results && response.results.length > 0) {
        return response.results[0];
      }
      toast({ title: 'Location not found', description: `Could not find "${place}".` });
    } catch (error) {
      console.error('Geocoding error:', error);
      toast({ title: 'Geocoding Error', description: 'Failed to look up location.', variant: 'destructive' });
    }
    return null;
  };

  const determineSearchType = (geocodingResult: google.maps.GeocoderResult): 'boundary' | 'proximity' | 'country' => {
    const types = geocodingResult.types || [];
    if (types.includes('country')) {
      return 'country';
    }
    if (types.includes('administrative_area_level_1') || types.includes('locality')) {
      return 'boundary';
    }
    return 'proximity';
  };

  const handleSearch = async () => {
    try {
      setSearchQuery(searchInput);

      const searchParams: {
        category: string;
        searchTerm?: string;
        location?: string;
        coords?: { lat: number; lng: number };
      } = {
        category: selectedCategory,
        searchTerm: searchInput,
      };

      if (locationSearch.trim()) {
        const geocodeResult = await geocodeLocation(locationSearch);
        if (!geocodeResult) {
          setLocationCoords(null);
          return;
        }

        const searchType = determineSearchType(geocodeResult);

        if (searchType === 'country') {
          searchParams.location = locationSearch.trim();
          setLocationCoords(null);
        } else if (searchType === 'boundary') {
          searchParams.location = geocodeResult.formatted_address;
          setLocationCoords(null);
        } else if (!geocodeResult.geometry || !geocodeResult.geometry.location) {
          searchParams.location = geocodeResult.formatted_address;
          setLocationCoords(null);
        } else {
          const { lat, lng } = geocodeResult.geometry.location;
          const coords = { lat: lat(), lng: lng() };
          searchParams.coords = coords;
          setLocationCoords(coords);
        }
      } else {
        setLocationCoords(null);
      }

      setMerchants([]);
      setFilteredMerchants([]);
      setMerchantsPage(1);
      setMerchantsHasMore(true);
      loadMerchants(1, false, searchParams);
    } catch (error) {
      console.error('Error in handleSearch:', error);
      toast({
        title: 'Search Error',
        description: 'An error occurred while processing your search. Please try again.',
        variant: 'destructive',
      });
    }
  };

  const handleSearchKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const openMerchantStorefront = (id: string) => {
    navigate(`/merchant/${id}`, {
      state: {
        marketplaceReturn: buildMarketplaceReturnState({
          activeTab: 'stores',
          searchQuery,
          searchInput,
          locationSearch,
          selectedCategory,
          locationRadius,
          locationCoords,
        }),
      },
    });
  };

  const MerchantCard = ({ merchant }: { merchant: MerchantPreview }) => {
    if (!merchant.businessName) {
      return null;
    }

    const cityState = [merchant.address?.city, merchant.address?.state].filter(Boolean).join(', ');

    return (
      <Card
        className="cursor-pointer hover:shadow-lg transition-shadow"
        onClick={() => openMerchantStorefront(merchant.id)}
      >
        <CardHeader>
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <CardTitle className="text-lg flex items-center space-x-2">
                <Store className="w-5 h-5" />
                <span>{merchant.businessName}</span>
                {merchant.verified && (
                  <Badge variant="secondary" className="text-xs">
                    <CheckCircle className="w-3 h-3 mr-1" />
                    Verified
                  </Badge>
                )}
              </CardTitle>
              {merchant.businessType && (
                <Badge variant="outline" className="mt-1 text-xs">
                  {merchant.businessType}
                </Badge>
              )}
            </div>
            {merchant.acceptsPayments && (
              <Badge className="bg-green-100 text-green-800">
                Accepting Payments
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {merchant.description && (
            <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
              {merchant.description}
            </p>
          )}

          {cityState && (
            <div className="flex items-center text-sm text-muted-foreground mb-2">
              <MapPin className="w-3 h-3 mr-1" />
              <span>{cityState}</span>
            </div>
          )}

          {merchant.rating != null && (
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-1">
                {[...Array(5)].map((_, i) => (
                  <Star
                    key={i}
                    className={`w-3 h-3 ${
                      i < Math.floor(merchant.rating || 0)
                        ? 'fill-yellow-500 text-yellow-500'
                        : 'text-gray-300'
                    }`}
                  />
                ))}
                <span className="text-sm text-muted-foreground ml-1">
                  {merchant.rating.toFixed(1)}
                </span>
              </div>
              {merchant.reviewCount && (
                <span className="text-xs text-muted-foreground">
                  ({merchant.reviewCount} reviews)
                </span>
              )}
            </div>
          )}

          {merchant.tags && merchant.tags.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-3">
              {merchant.tags.slice(0, 3).map(tag => (
                <Badge key={tag} variant="secondary" className="text-xs">
                  {tag}
                </Badge>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="container mx-auto px-4 py-8 max-w-7xl overflow-x-hidden">
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2">{i18n.__('marketplace.title')}</h1>
        <p className="text-muted-foreground">
          {i18n.__('marketplace.subtitle')}
        </p>
      </div>

      <div className="flex flex-col lg:flex-row gap-6">
        <div className="lg:w-64 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">{i18n.__('marketplace.filters.title')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>{i18n.__('marketplace.filters.category')}</Label>
                <Select
                  value={selectedCategory}
                  onValueChange={setSelectedCategory}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{i18n.__('marketplace.filters.allCategories')}</SelectItem>
                    {STORE_CATEGORIES.map(category => (
                      <SelectItem key={category.value} value={category.value}>
                        {i18n.__(category.labelKey as any)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>{i18n.__('marketplace.filters.locationRadius')}</Label>
                <Select
                  value={locationRadius.toString()}
                  onValueChange={(value) => {
                    setLocationRadius(value === 'worldwide' ? 'worldwide' : parseInt(value));
                  }}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="10">{i18n.__('marketplace.filters.radius.10')}</SelectItem>
                    <SelectItem value="25">{i18n.__('marketplace.filters.radius.25')}</SelectItem>
                    <SelectItem value="50">{i18n.__('marketplace.filters.radius.50')}</SelectItem>
                    <SelectItem value="100">{i18n.__('marketplace.filters.radius.100')}</SelectItem>
                    <SelectItem value="250">{i18n.__('marketplace.filters.radius.250')}</SelectItem>
                    <SelectItem value="500">{i18n.__('marketplace.filters.radius.500')}</SelectItem>
                    <SelectItem value="worldwide">{i18n.__('marketplace.filters.radius.worldwide')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Button
                variant="outline"
                className="w-full"
                onClick={() => {
                  setSearchInput('');
                  setSearchQuery('');
                  setSelectedCategory('all');
                  setLocationRadius(50);
                }}
              >
                {i18n.__('marketplace.filters.clear')}
              </Button>
            </CardContent>
          </Card>
        </div>

        <div className="flex-1">
          <div className="mb-6 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                <Input
                  placeholder={i18n.__('marketplace.search.storesPlaceholder')}
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  onKeyPress={handleSearchKeyPress}
                  className="pl-10 w-full"
                />
              </div>

              <div className="relative">
                <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                <Input
                  placeholder={i18n.__('marketplace.search.locationPlaceholder')}
                  value={locationSearch}
                  onChange={(e) => setLocationSearch(e.target.value)}
                  onKeyPress={handleSearchKeyPress}
                  className="pl-10 w-full"
                />
              </div>

              <div className="flex gap-2">
                <Button
                  onClick={handleSearch}
                  disabled={merchantsLoading}
                  variant="default"
                  size="default"
                  className="flex-1"
                >
                  {merchantsLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      {i18n.__('marketplace.search.searching')}
                    </>
                  ) : (
                    <>
                      <Search className="w-4 h-4 mr-2" />
                      {i18n.__('marketplace.search.button')}
                    </>
                  )}
                </Button>
                {(searchInput || locationSearch) && (
                  <Button
                    onClick={() => {
                      setSearchInput('');
                      setLocationSearch('');
                      setSearchQuery('');
                      setMerchants([]);
                      setFilteredMerchants([]);
                      setMerchantsPage(1);
                      setMerchantsHasMore(true);
                      loadMerchants(1, false, { searchTerm: '', category: 'all' });
                    }}
                    variant="outline"
                    size="default"
                  >
                    {i18n.__('common.clear')}
                  </Button>
                )}
              </div>
            </div>

            <Select value={sortBy} onValueChange={(value) => setSortBy(value as 'name' | 'rating')}>
              <SelectTrigger className="w-full sm:w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="name">{i18n.__('marketplace.sort.name')}</SelectItem>
                <SelectItem value="rating">{i18n.__('marketplace.sort.rating')}</SelectItem>
              </SelectContent>
            </Select>

            <p className="text-sm text-muted-foreground">
              {i18n.__('marketplace.results.stores' as any)
                .replace('{{count}}', filteredMerchants.length.toString())
                .replace('{{total}}', (merchantsTotal || merchants.length).toString())}
            </p>
          </div>

          {merchantsLoading && merchants.length === 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => <MerchantCardSkeleton key={i} />)}
            </div>
          ) : filteredMerchants.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Store className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                <h3 className="text-lg font-semibold mb-2">
                  {merchantsLoadFailed
                    ? i18n.__('marketplace.results.storesUnavailableTitle')
                    : i18n.__('marketplace.results.noStores')}
                </h3>
                <p className="text-muted-foreground max-w-md mx-auto">
                  {merchantsLoadFailed
                    ? i18n.__('marketplace.results.storesUnavailableHint')
                    : i18n.__('marketplace.results.noStoresHint')}
                </p>
                {merchantsLoadFailed && (
                  <Button
                    className="mt-6"
                    variant="secondary"
                    disabled={merchantsLoading}
                    onClick={() => {
                      loadMerchants(1, false, {
                        searchTerm: searchQuery,
                        category: selectedCategory,
                        ...(locationCoords ? { coords: locationCoords } : {}),
                      });
                    }}
                  >
                    {merchantsLoading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        {i18n.__('marketplace.search.searching')}
                      </>
                    ) : (
                      i18n.__('marketplace.results.storesRetry')
                    )}
                  </Button>
                )}
              </CardContent>
            </Card>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredMerchants.map(merchant => (
                  <MerchantCard key={merchant.id} merchant={merchant} />
                ))}
              </div>

              {merchantsHasMore && (
                <div
                  ref={loadMoreTriggerRef}
                  className="flex justify-center py-8"
                >
                  {merchantsLoading ? (
                    <div className="flex items-center space-x-2">
                      <Loader2 className="w-6 h-6 animate-spin" />
                      <span>{i18n.__('marketplace.results.loadingMoreStores')}</span>
                    </div>
                  ) : (
                    <div className="text-sm text-muted-foreground">
                      {i18n.__('marketplace.results.scrollMore')}
                    </div>
                  )}
                </div>
              )}

              {!merchantsHasMore && filteredMerchants.length > 0 && (
                <div className="text-center py-8 text-sm text-muted-foreground">
                  {i18n.__('marketplace.results.showingAllStores' as any)
                    .replace('{{total}}', merchantsTotal.toString())}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
